import { LayoutGrid, Settings } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { useBooks } from '../hooks/useBooks'
import type { Book, BookRole } from '../types'
import './BookSwitcher.css'

const ROLE_ORDER: BookRole[] = ['research', 'validation', 'live']
const ROLE_LABEL: Record<BookRole, string> = {
  research: 'Research',
  validation: 'Validation',
  live: 'Live',
  book: 'Book',
}

/** Dot status + tooltip derived from a Book's bookStatus. */
function dotState(book: Book): { cls: string; title: string } {
  const role = ROLE_LABEL[book.role] ?? book.role
  if (book.bookStatus === null) {
    return { cls: 'dormant', title: `${role} — dormant (not built)` }
  }
  if (book.bookStatus.halted) {
    return {
      cls: 'halted',
      title: `${role} — halted${book.bookStatus.halt_reason ? `: ${book.bookStatus.halt_reason}` : ''}`,
    }
  }
  return { cls: 'running', title: `${role} — running` }
}

/**
 * Horizontal pill nav: Fleet + one pill per role (Research, Validation,
 * Live, fixed order) + Ops. A role with no matching book renders a disabled
 * ghost pill. Status dot color mirrors bookStatus (running/halted/absent).
 * Ops uses the same Settings icon as the phone's bottom nav.
 */
export function BookSwitcher() {
  const { byRole } = useBooks()

  return (
    <nav className="book-switcher" aria-label="Book switcher">
      <NavLink
        to="/"
        end
        className={({ isActive }) => `book-pill book-pill--fleet focus-ring${isActive ? ' active' : ''}`}
      >
        <LayoutGrid size={14} aria-hidden="true" />
        Fleet
      </NavLink>

      <span className="book-switcher-divider" aria-hidden="true" />

      {ROLE_ORDER.map((role) => {
        const book = byRole(role)
        if (!book) {
          return (
            <span
              key={role}
              className="book-pill book-pill--disabled"
              title="not yet created"
              aria-disabled="true"
            >
              <span className="book-pill-dot dormant" aria-hidden="true" />
              {ROLE_LABEL[role]}
            </span>
          )
        }
        const { cls, title } = dotState(book)
        return (
          <NavLink
            key={book.id}
            to={`/book/${book.id}`}
            title={title}
            className={({ isActive }) =>
              `book-pill book-pill--${role} focus-ring${cls === 'dormant' ? ' muted' : ''}${isActive ? ' active' : ''}`
            }
          >
            <span className={`book-pill-dot ${cls}`} aria-hidden="true" />
            {ROLE_LABEL[role]}
          </NavLink>
        )
      })}

      <span className="book-switcher-divider" aria-hidden="true" />

      <NavLink
        to="/ops"
        className={({ isActive }) => `book-pill book-pill--ops focus-ring${isActive ? ' active' : ''}`}
      >
        <Settings size={14} aria-hidden="true" />
        Ops
      </NavLink>
    </nav>
  )
}
